import React, { useState, useEffect } from "react";
import UserService from "../../services/user";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Backdrop from "../../components/Backdrop";

const Register = ({ setShowRegister, onRegisterSuccess }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validationSchema = yup.object({
    full_name: yup.string("Enter fullname").required("Fullname is required"),
    mobile_no: yup
      .string("Enter Mobile number")
      .required("Mobile number is required"),
    email: yup.string("Enter email").email().required("Email is required"),
    password: yup.string("Enter Password").required("Password is required"),
  });

  const [backdrop, setBackdrop] = useState(false);

  const formik = useFormik({
    initialValues: {
      full_name: "",
      password: "",
      mobile_no: "",
      email: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setBackdrop(true);
      try {
        // Register the user directly without OTP
        let { status } = await UserService.register(values);
        if (status) {
          toast.success("Registration successful! Please login to continue.");
          // Pass the email to login form and switch to login
          onRegisterSuccess(values.email);
          setShowRegister(false); // Close register form and show login
        }
      } catch (err) {
        // Handle specific error cases
        const errorMessage = err?.response?.data?.message;
        if (errorMessage && errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('exist')) {
          toast.error("User with this email already exists!");
        } else {
          toast.error(errorMessage || "Something went wrong!");
        }
      }
      setBackdrop(false);
    },
  });

  const handleBackToLogin = () => {
    setShowRegister(false);
  };

  return (
    <>
      <style jsx>{`
        .register-container {
          min-height: 100vh;
          background-color: #f9fafb;
          display: flex;
          flex-direction: column;
        }

        @media (min-width: 768px) {
          .register-container {
            flex-direction: row;
          }
        }

        .left-panel {
          width: 100%;
          background-color: #ffffff;
          position: relative;
          min-height: 50vh;
        }

        @media (min-width: 768px) {
          .left-panel {
            width: 50%;
            min-height: 100vh;
          }
        }

        .right-panel {
          width: 100%;
          background: #d0efe1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 2rem;
          min-height: 50vh;
        }

        @media (min-width: 768px) {
          .right-panel {
            width: 50%;
            min-height: 100vh;
          }
        }

        @media (max-width: 768px) {
          .left-panel {
            min-height: 30vh;
          }

          .right-panel {
            min-height: 70vh;
            padding: 2rem 1rem;
          }
        }

        .content-wrapper {
          max-width: 24rem;
          text-align: center;
          width: 100%;
        }

        .welcome-title {
          font-size: 2rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .welcome-subtitle {
          color: #6b7280;
          font-size: 1rem;
          margin-bottom: 2rem;
        }

        .register-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .input-wrapper {
          position: relative;
        }

        .input-field {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          background-color: #ffffff;
          border-radius: 9999px;
          border: 1px solid #e5e7eb;
          outline: none;
          font-size: 1rem;
          transition: all 0.2s;
          box-sizing: border-box;
        }

        .input-field:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .error {
          color: #ef4444;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          margin-left: 1rem;
        }

        .btn-register {
          width: 100%;
          background-color: #1f2937;
          color: #ffffff;
          padding: 1rem;
          border-radius: 9999px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
          font-size: 1rem;
          margin-top: 1.5rem;
        }

        .btn-register:hover {
          background-color: #374151;
        }

        .login-link {
          text-align: center;
          margin-top: 1rem;
          color: #6b7280;
          cursor: pointer;
          font-size: 1rem;
          transition: color 0.2s;
        }

        .login-link:hover {
          color: #1f2937;
        }

        .login-link a {
          color: #1f2937;
          text-decoration: none;
          font-weight: 600;
        }

        .login-link a:hover {
          text-decoration: underline;
        }
      `}</style>

      {backdrop && <Backdrop />}

      <div className="register-container">
        {/* Left Panel - Background Image */}
        <div className="left-panel">
          <img
            src="/LoginDesktop.png"
            alt="Register Background"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              top: 0,
              left: 0
            }}
          />
        </div>

        {/* Right Panel - Register Form */}
        <div className="right-panel">
          <div className="content-wrapper">
            {/* Welcome Text */}
            <div style={{ textAlign: "center" }}>
              <h1 className="welcome-title">Create Account</h1>
              <h2 className="welcome-subtitle">Welcome to Shaagird</h2>
            </div>

            {/* Register Form */}
            <form onSubmit={formik.handleSubmit} className="register-form">
              <label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formik.values.full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="full_name"
                    className="input-field"
                    required
                  />
                  <div className="input-icon">
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                </div>
                {formik.touched.full_name && formik.errors.full_name && (
                  <div className="error">{formik.errors.full_name}</div>
                )}
              </label>

              <label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="email"
                    className="input-field"
                    required
                  />
                  <div className="input-icon">
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
                {formik.touched.email && formik.errors.email && (
                  <div className="error">{formik.errors.email}</div>
                )}
              </label>

              <label>
                <div className="input-wrapper">
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    value={formik.values.mobile_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="mobile_no"
                    className="input-field"
                    required
                  />
                  <div className="input-icon">
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                </div>
                {formik.touched.mobile_no && formik.errors.mobile_no && (
                  <div className="error">{formik.errors.mobile_no}</div>
                )}
              </label>

              <label>
                <div className="input-wrapper">
                  <input
                    type="password"
                    placeholder="Password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="password"
                    className="input-field"
                    required
                  />
                  <div className="input-icon">
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className="error">{formik.errors.password}</div>
                )}
              </label>

              <button type="submit" className="btn-register">
                Register
              </button>
            </form>

            <p className="login-link">
              Already have an account?{" "}
              <Link onClick={handleBackToLogin}>Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
